'use client';
import { useEffect, useState } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import Script from 'next/script';

const GTM_ID = 'GTM-PRPR8QKJ';
const YM_ID = 112160723; 
const isProduction = process.env.NODE_ENV === 'production';

declare global {
  interface Window {
    dataLayer?: any[];
    ym?: (id: number, action: string, ...args: any[]) => void;
    // Типизация для твоей аналитики
    RyoTelemetry?: any;
    ryoAnalytics?: any;
  }
}

// --- Инициализаторы сторонних систем ---
const initGTM = () => {
  if (typeof window === 'undefined' || !GTM_ID) return;
  if (document.getElementById('gtm-script')) return;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    'gtm.start': new Date().getTime(),
    event: 'gtm.js',
  });

  const script = document.createElement('script');
  script.id = 'gtm-script';
  script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
  script.async = true;
  document.head.appendChild(script);
};

const initYandexMetrika = () => {
  if (typeof window === 'undefined' || !YM_ID) return;
  if (typeof window.ym !== 'undefined') return;

  const script = document.createElement('script');
  script.innerHTML = `
    (function(m,e,t,r,i,k,a){m[i]=m[i]||function(){(m[i].a=m[i].a||[]).push(arguments)};
    m[i].l=1*new Date();
    for (var j = 0; j < document.scripts.length; j++) {if (document.scripts[j].src === r) { return; }}
    k=e.createElement(t),a=e.getElementsByTagName(t)[0],k.async=1,k.src=r,a.parentNode.insertBefore(k,a)})
    (window, document, "script", "https://mc.webvisor.org/metrika/tag_ww.js?id=${YM_ID}", "ym");

    ym(${YM_ID}, "init", { clickmap:true, trackLinks:true, accurateTrackBounce:true, webvisor:true });
  `;
  document.head.appendChild(script);
};

// --- Функции трекинга ---
const pageviewGTM = (url: string) => {
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push({ event: 'page_view', page_path: url });
  }
};

const yandexHit = (url: string) => {
  if (typeof window !== 'undefined' && typeof window.ym !== 'undefined') {
    window.ym(YM_ID, 'hit', url);
  }
};

export default function Analytics() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  
  const [consentGiven, setConsentGiven] = useState(false);
  const [isRyoReady, setIsRyoReady] = useState(false);

  // 1. Проверяем согласие на куки
  useEffect(() => {
    if (typeof window === 'undefined') return; // RyoTelemetry работает и на dev, GTM/YM только на production

    try {
      const stored = localStorage.getItem('cookie_consent_status');
      if (stored === 'granted') setConsentGiven(true);
    } catch (e) {}

    const handleConsentEvent = () => setConsentGiven(true);
    window.addEventListener('royolands_consent_granted', handleConsentEvent);
    return () => window.removeEventListener('royolands_consent_granted', handleConsentEvent);
  }, []);

  // 2. Инициализация сторонних трекеров (только если есть согласие и это прод)
  useEffect(() => {
    if (consentGiven && isProduction) {
      initGTM();
      initYandexMetrika();
    }
  }, [consentGiven]);

  // 3. Инициализация нашего RyoTelemetry (вызывается после загрузки скрипта из <Script>)
  const handleRyoScriptLoad = () => {
    // Включаем RyoTelemetry вне зависимости от согласия, т.к. это наша внутренняя системная аналитика
    if (typeof window !== 'undefined' && window.RyoTelemetry && !window.ryoAnalytics) {
      window.ryoAnalytics = new window.RyoTelemetry({
        appId: 'rplands_frontend',
        uploadHost: 'https://log-upload-os.royolands.com',
        gameBiz: 'royolands',
        publicKey: 'ryolands-public-sdk-key-2024',
        autoTrackPageview: false // Отключаем авто-запуск, Next.js сделает это сам!
      });
      setIsRyoReady(true);
    }
  };

  // 4. Трекинг переходов (SPA роутинг Next.js)
  useEffect(() => {
    if (!pathname) return;
    const url = pathname + (searchParams?.toString() ? `?${searchParams.toString()}` : '');

    // Трекаем во внешние системы, если есть согласие
    if (consentGiven && isProduction) {
      pageviewGTM(url);
      yandexHit(url);
    }

    // Трекаем в нашу систему, если скрипт загрузился
    if (isRyoReady && window.ryoAnalytics) {
      window.ryoAnalytics.trackEvent('page', 'view', url);
    }
  }, [pathname, searchParams, consentGiven, isRyoReady]);

  return (
    <Script
      src="https://rplands.royolands.com/telemetry3.js"
      strategy="afterInteractive"
      onLoad={handleRyoScriptLoad}
    />
  );
}